import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Button,
  LinearProgress,
} from '@mui/material';
import CloseIcon from '@material-ui/icons/Close';
import { COLOR_PRIMARY } from 'src/const';

interface TutorialModalProps {
  open: boolean;
  onClose: () => void;
  type: 'market_research' | 'neighborhood_research';
}

const tutorialContent = {
  market_research: {
    title: "Find the Best Airbnb Markets with Airbtics' Market Comparison Tool",
    videoId: 'uU9cqAQbvBs',
  },
  neighborhood_research: {
    title: 'Explore Airbnb Neighborhoods with Heatmaps, Clusters, and Listing Insights ',
    videoId: 'MeQL34SOROE',
  },
};

const TutorialModal: React.FC<TutorialModalProps> = ({ open, onClose, type }) => {
  const [progress, setProgress] = useState(0);
  const content = tutorialContent[type];

  useEffect(() => {
    if (open) {
      setProgress(0);
      const timer = setInterval(() => {
        setProgress(prevProgress => {
          if (prevProgress >= 100) {
            clearInterval(timer);
            return 100;
          }
          return prevProgress + 1;
        });
      }, 150);

      return () => {
        clearInterval(timer);
      };
    }
    return () => {};
  }, [open]);

  useEffect(() => {
    return () => {
      localStorage.removeItem(
        type === 'market_research' ? 'show_market_compare_tutorial' : 'show_neighborhood_tutorial'
      );
    };
  }, []);

  const handleContinue = () => {
    localStorage.removeItem(
      type === 'market_research' ? 'show_market_compare_tutorial' : 'show_neighborhood_tutorial'
    );
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: 3,
          maxWidth: '800px',
          width: '90vw',
          margin: 2,
        },
        zIndex: 10000,
      }}
    >
      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 1,
          top: 1,
          color: 'grey.500',
          zIndex: 1,
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent sx={{ p: '16px 24px 24px', overflow: 'hidden' }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            mb: 1,
            color: 'text.primary',
          }}
        >
          {content.title}
        </Typography>

        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            mb: 2,
            height: 4,
            borderRadius: 1,
          }}
          color="primary"
        />

        <Box
          sx={{
            position: 'relative',
            paddingBottom: '56.25%', // 16:9 aspect ratio
            height: 0,
            overflow: 'hidden',
            borderRadius: 2,
            mb: 2,
            bgcolor: '#000',
            '& iframe': {
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              border: 'none',
            },
          }}
        >
          <iframe
            src={`https://www.youtube.com/embed/${content.videoId}?autoplay=1&rel=0`}
            title="Tutorial Video"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2,
          }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              px: 3,
              py: 1,
            }}
          >
            Watch Later
          </Button>
          <Button
            variant="contained"
            onClick={handleContinue}
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              px: 3,
              py: 1,
              bgcolor: COLOR_PRIMARY,
              color: '#fff',
              '&:hover': {
                bgcolor: COLOR_PRIMARY,
              },
            }}
          >
            Continue to Dashboard
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default TutorialModal;
